import React, { useEffect } from "react";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { LocalLevelError } from "@app.automotus.io/components/common/LocalLevelError";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ImageCarousel } from "@app.automotus.io/components/ImageCarousel";
import type { CarouselSlide } from "@app.automotus.io/components/ImageCarousel";
import {
  GET_TRANSACTION_DETAIL,
  GET_TRANSACTION_IMAGES,
  GetTransactionDetailData,
  GetTransactionDetailVars,
  GetTransactionImagesData,
  GetTransactionImagesVars,
} from "common/graphql";
import { useMutation, useQuery } from "@apollo/client";
import LoadingSkeleton from "../LoadingSkeleton";
import { Theme } from "@mui/material/styles";
import { firstCaptureSlideTitle } from "./firstCaptureSlideTitle";

/**
 * Displays a header/title component in the detailed transaction display for a payee user.
 */
export const TransactionHeader: React.FC<TransactionHeaderProps> = ({
  transactionId,
  disableImageCarousel = false,
  stackViolationLabel = false,
}) => {
  const {
    data: transactionData,
    loading: transactionLoading,
    error: transactionError,
    refetch: refetchTransaction,
  } = useQuery<GetTransactionDetailData, GetTransactionDetailVars>(GET_TRANSACTION_DETAIL, {
    variables: {
      transactionId,
    },
  });

  const transactionTags = transactionData?.transaction?.tags.map(({ tag }) => tag) || [];

  const isViolation = Boolean(transactionTags.find((t) => Boolean(t.match(/violation/))));

  const [getTransactionImages, { data: imagesData, loading: imagesLoading, error: getImagesError }] = useMutation<
    GetTransactionImagesData,
    GetTransactionImagesVars
  >(GET_TRANSACTION_IMAGES, {
    onError(err) {
      console.error("failed to fetch transaction images", err);
    },
  });

  useEffect(() => {
    getTransactionImages({ variables: { transactionId } });
  }, [getTransactionImages, transactionId]);

  // The get_transaction_image_urls action returns the same NotFound code whether the
  // transaction doesn't exist, has no image data (true for every non-park/double_park
  // transaction type), or the caller isn't authorized to see it -- none of those should
  // ever show image data, but none of them warrant hiding the whole header behind a
  // full-page error either. `slides` below already resolves to [] when imagesData is
  // undefined, so ImageCarousel's own "No Images" state handles this correctly on its own.
  const isImagesNotFound = getImagesError?.graphQLErrors?.some(
    (gqlErr) => (gqlErr.extensions?.code ?? (gqlErr.extensions?.internal as { code?: string })?.code) === "NotFound",
  );
  const error = transactionError || (getImagesError && !isImagesNotFound ? getImagesError : undefined);
  const loading = imagesLoading || transactionLoading;

  const slides: CarouselSlide[] = (
    [
      transactionData?.transaction?.sessionStartedAtLocal &&
        imagesData?.transactionImages?.licensePlateImageUrl && {
          title: firstCaptureSlideTitle(transactionData.transaction.transactionType),
          date: new Date(transactionData.transaction.sessionStartedAtLocal),
          imageSrc: imagesData.transactionImages.licensePlateImageUrl,
        },
      transactionData?.transaction?.sessionStartedAtLocal &&
        imagesData?.transactionImages?.sessionStartImageUrl && {
          title: "Session Start",
          date: new Date(transactionData.transaction.sessionStartedAtLocal),
          imageSrc: imagesData.transactionImages.sessionStartImageUrl,
        },
      transactionData?.transaction?.sessionEndedAtLocal &&
        imagesData?.transactionImages?.sessionEndImageUrl && {
          title: "Session End",
          date: new Date(transactionData.transaction.sessionEndedAtLocal),
          imageSrc: imagesData.transactionImages.sessionEndImageUrl,
        },
    ] as (CarouselSlide | false | undefined)[]
  ).filter((v): v is CarouselSlide => Boolean(v));

  return (
    <Paper sx={{ p: 3 }}>
      {error ? (
        <LocalLevelError
          onTryAgain={() => {
            getTransactionImages({ variables: { transactionId } });
            refetchTransaction();
          }}
        />
      ) : (
        <Stack spacing={3}>
          <LoadingSkeleton loading={loading}>
            <Box
              display={"flex"}
              flexDirection={stackViolationLabel ? "column" : "row"}
              justifyContent={stackViolationLabel ? "flex-start" : "space-between"}
              alignItems={stackViolationLabel ? "flex-start" : "center"}
            >
              <Typography
                variant={"h5"}
                sx={{ flex: 1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", fontWeight: 400 }}
              >
                Transaction ID: {transactionData?.transaction?.index.toString().padStart(6, "0")}
              </Typography>
              {isViolation && (
                <Typography
                  sx={{
                    color: (theme: Theme) => theme.palette.error.main,
                    ml: stackViolationLabel ? 0 : 2,
                    mt: stackViolationLabel ? 0.5 : 0,
                  }}
                  variant={"h7"}
                  fontWeight={600}
                >
                  Violation
                </Typography>
              )}
            </Box>
          </LoadingSkeleton>
          {disableImageCarousel || <ImageCarousel loading={loading} slides={slides} />}
        </Stack>
      )}
    </Paper>
  );
};

export interface TransactionHeaderProps {
  /** ID of the transaction. It is assumed that this is already available prior to loading the transaction */
  transactionId: string;
  /** Disables display of image carousel when set. */
  disableImageCarousel?: boolean;
  /** Stacks the Violation badge onto its own line below the title instead of inline, at every screen size. */
  stackViolationLabel?: boolean;
}

export default TransactionHeader;
